import * as React from 'react'
import { useCallback, useState } from 'react'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import { ManageRecordSection } from '../ManageRecordSection'
import { useData } from './hooks/useData'
import {
  LeadAutoCloseMissingFieldsBanner,
  RequiredFieldsDialog,
} from '@user/components/LeadAutoClose'

export function LeadAutoCloseSection({ entityId }: { entityId: number }) {
  const [requiredFieldsOpen, setRequiredFieldsOpen] = useState(false)

  const {
    value,
    onChange,
    enabled,
    willAutoClose,
    requiredFieldsAreMissing,
    missingScheduleAutoCloseRequiredFields,
  } = useData()

  const onOpenRequiredFields = useCallback(
    () => setRequiredFieldsOpen(true),
    [],
  )

  const onCloseRequiredFields = useCallback(
    () => setRequiredFieldsOpen(false),
    [],
  )

  if (!enabled) return null

  return (
    <ManageRecordSection>
      <Stack width="100%" direction="column" spacing={1} mb={2}>
        <>
          {willAutoClose ? (
            <Alert
              severity="info"
              sx={{ textAlign: 'left' }}
              data-test-id="lead-auto-close-alert-info"
            >
              This lead will be automatically submitted to the closed/won queue
              upon scheduling service.
            </Alert>
          ) : (
            <Box px={1.2}>
              <RadioGroup value={value} onChange={onChange}>
                <FormControlLabel
                  value="close"
                  control={<Radio />}
                  disabled={!willAutoClose}
                  label="Close win this sale upon scheduling"
                  data-test-id="lead-auto-close-radio-close-win"
                />
                <FormControlLabel
                  value="open"
                  control={<Radio />}
                  label="Keep this record open to allow additional updates"
                  data-test-id="lead-auto-close-radio-keep-open"
                />

                {requiredFieldsAreMissing ? (
                  <LeadAutoCloseMissingFieldsBanner
                    count={missingScheduleAutoCloseRequiredFields.length}
                    onUpdateRequiredFields={onOpenRequiredFields}
                  />
                ) : (
                  <Alert
                    severity="warning"
                    sx={{ textAlign: 'left', ml: 3.2 }}
                    data-test-id="lead-auto-close-alert-warning"
                  >
                    <Typography variant="body2">
                      Closing requirements have not been satisfied to Close Win
                      upon scheduling. You can cancel and complete the closing
                      requirements, or continue to schedule service without
                      closing and sending to the Closed Won Queue
                    </Typography>
                  </Alert>
                )}
              </RadioGroup>
            </Box>
          )}
        </>
      </Stack>

      {requiredFieldsOpen && (
        <RequiredFieldsDialog
          leadId={entityId}
          missingFieldIds={missingScheduleAutoCloseRequiredFields}
          onClose={onCloseRequiredFields}
        />
      )}
    </ManageRecordSection>
  )
}

LeadAutoCloseSection.displayName = 'LeadAutoCloseSection'
